import { NegotiationObjective } from '@procureai/constellation/clara/negotiation-case/v1.avsc';
import {
  NegotiationInitiationMode,
  NegotiationMode,
  NegotiationSupplierInteractionMode,
  QuoteMode,
} from '@procureai/schemas';
import {
  biddingAndNegotiationSettingsSchema,
  BiddingAndNegotiationSettings,
  UseEditableWidgetBackend,
} from '@procureai/ui-modules-mui';

import {
  TsrctSourcingCaseInitiationMode,
  TsrctSourcingCaseNegotiationObjective,
  TsrctSourcingCaseNegotiationStrategyBoundaryBase,
  TsrctSourcingCaseSupplierInteractionMode,
} from '@/generated/gql/graphql';
import { createEditableWidgetGraphqlBackendHook } from '@/shared/hooks/useEditableWidgetGraphqlBackend/useEditableWidgetGraphqlBackend';
import { isSourcingCaseEditable } from '~/common/guards/sourcingCase.guards';
import { useModuleContext } from '~/components/ModuleContextProvider';

import { SOURCING_CASE_NEGOTIATION_SETTINGS_MUTATION } from './SourcingCaseNegotiationSettingsWidget.mutation.gql';
import { SOURCING_CASE_NEGOTIATION_SETTINGS_QUERY } from './SourcingCaseNegotiationSettingsWidget.query.gql';

const useSourcingCaseNegotiationSettingsWidgetBackendInternal = createEditableWidgetGraphqlBackendHook({
  schema: biddingAndNegotiationSettingsSchema,
  query: SOURCING_CASE_NEGOTIATION_SETTINGS_QUERY,
  mutation: SOURCING_CASE_NEGOTIATION_SETTINGS_MUTATION,
});

export const useSourcingCaseNegotiationSettingsWidgetBackend: UseEditableWidgetBackend<
  BiddingAndNegotiationSettings
> = ({ i18n }) => {
  const { entityId } = useModuleContext();
  const variables = { id: entityId };

  return useSourcingCaseNegotiationSettingsWidgetBackendInternal({
    i18n,
    variables,
    skip: !entityId,
    toFormData: (data) => {
      const sourcingCase = data?.tesseract.sourcingCase;
      return {
        negotiationEnabled: sourcingCase.isNegotiationEnabled,
        supplierUploadEnabled: sourcingCase.isSupplierUploadAllowed ?? false,
        initiatedBy: sourcingCase.negotiationOptions?.initiatedBy ?? NegotiationInitiationMode.PROCUREAI,
        strategy: sourcingCase.negotiationOptions?.negotiationObjective as unknown as NegotiationObjective,
        supplierInteractionMode: sourcingCase.negotiationOptions
          ?.supplierInteractionMode as unknown as NegotiationSupplierInteractionMode,
        boundaries: sourcingCase.negotiationStrategy?.boundaryRules?.map((b) => ({
          base: b.base as TsrctSourcingCaseNegotiationStrategyBoundaryBase,
          lower: Number(b.lower),
          upper: Number(b.upper),
        })) ?? [
          {
            base: 'QUOTED_PRICE' as TsrctSourcingCaseNegotiationStrategyBoundaryBase,
            // Force as unknown to force validation to trigger in the form
            lower: undefined,
            upper: undefined,
          },
        ],
        boundaryAdjustments: {
          historicNegotiations: sourcingCase.negotiationStrategy?.useHistoricNegotiationData ?? false,
          historicTenderData: sourcingCase.negotiationStrategy?.useHistoricTenderData ?? false,
        },
        // fill not visible fields with default values
        // so that validation does not fail when the form is submitted
        // even if the user did not interact with the form
        // TODO remove when we have a more generic way to handle form fields visibility
        negotiationMode: NegotiationMode.QUOTE,
        quoteMode: QuoteMode.BID_SHEET,
      };
    },
    toMutationData: (formData) => {
      return {
        id: entityId,
        data: {
          isEnabled: formData.negotiationEnabled ?? false,
          isSupplierUploadAllowed: formData.supplierUploadEnabled ?? false,
          negotiationOptions: {
            initiatedBy: TsrctSourcingCaseInitiationMode.PROCUREAI,
            negotiationObjective: formData.strategy as unknown as TsrctSourcingCaseNegotiationObjective,
            supplierInteractionMode:
              formData.supplierInteractionMode as unknown as TsrctSourcingCaseSupplierInteractionMode,
          },
          negotiationStrategy: {
            // At least a boundary rule is always defined because of default values and validation
            boundaryRules: formData.boundaries.map((b, index) => ({
              base: b.base as TsrctSourcingCaseNegotiationStrategyBoundaryBase,
              lower: b.lower.toString(),
              upper: b.upper.toString(),
              sequence: index,
            })),
            useHistoricNegotiationData: formData.boundaryAdjustments?.historicNegotiations ?? false,
            useHistoricTenderData: formData.boundaryAdjustments?.historicTenderData ?? false,
          },
        },
      };
    },
    toSimpleFormWidgetProps: (data, loading) => {
      return loading || !isSourcingCaseEditable(data?.tesseract.sourcingCase)
        ? { disabled: true, loading }
        : { disabled: false, loading };
    },
  });
};
